// @ts-nocheck
import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * CLAUDE.md: never render `<input type="number">`. The spinner and the
 * scroll-wheel silently rewrite the value on an accidental scroll or trackpad
 * swipe, which corrupts data — and on the pricing screens that means a price
 * changing under the admin without them noticing (ADM-09-10).
 *
 * Rather than converting ~41 call sites by hand and hoping nobody adds a 42nd,
 * the conversion happens HERE, at the single component every admin form uses.
 * A call site may still pass `type="number"` to express intent; it renders as a
 * sanitized text input with the right mobile keypad.
 *
 * Decimals are allowed when the call site asks for them — either
 * `inputMode="decimal"` or a fractional `step` (e.g. `step="0.01"` on money).
 * Otherwise input is digits-only.
 *
 * NOTE: negatives are intentionally not accepted. Every numeric field in the
 * admin today is a non-negative quantity (counts, scores, capacities, durations,
 * money). If a genuinely signed field is ever added, widen the pattern here
 * deliberately rather than reaching back for `type="number"`.
 */
const sanitizeNumeric = (raw, allowDecimal) =>
  String(raw).replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, "")

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, inputMode, onChange, step, min, max, ...props }, ref) => {
    const isNumeric = type === "number"
    const allowDecimal =
      isNumeric &&
      (inputMode === "decimal" || (step != null && String(step).includes(".")))

    const handleChange = React.useCallback(
      (e) => {
        if (isNumeric) {
          const cleaned = sanitizeNumeric(e.target.value, allowDecimal)
          // Rewrite before delegating so every existing handler — which reads
          // `e.target.value` — receives the sanitized string and needs no change.
          if (cleaned !== e.target.value) e.target.value = cleaned
        }
        onChange?.(e)
      },
      [isNumeric, allowDecimal, onChange]
    )

    // `step`/`min`/`max` only mean something to a real number input; forwarding
    // them onto a text input would put misleading attributes in the DOM.
    const numericAttrs = isNumeric ? {} : { step, min, max }

    return (
      <input
        type={isNumeric ? "text" : type}
        inputMode={isNumeric ? (allowDecimal ? "decimal" : "numeric") : inputMode}
        onChange={handleChange}
        {...numericAttrs}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
